import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Building,
    Copy,
    CreditCard,
    Database,
    ExternalLink,
    EyeOff,
    FileText,
    Globe,
    Image,
    KeyRound,
    Languages,
    ListChecks,
    Loader2,
    Save,
    Search,
    ShieldCheck,
    SpellCheck,
    Tags,
    UploadCloud,
    UserRoundCog,
    Webhook,
} from 'lucide-react';
import { useSettings, SettingsState, AppLanguage, type CustomDataField, type DataLabel, type ImportantDataField, type SearchResultRule, type SubscriptionInvoice } from '../../hooks/useSettings';
import { languageLabels, supportedLanguages, useI18n } from '../../i18n/LanguageContext';
import { useApiClient } from '../../lib/apiClient';
import { formatMoney } from '../../i18n/orgFormat';
import { AddressFields } from '../../components/AddressFields';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const urlPattern = /^https?:\/\//i;

export default function SettingsProfile() {
    const navigate = useNavigate();
    const { settings, setSettings, load, save, loading, error } = useSettings();
    const { fetchJson } = useApiClient();
    const { t } = useI18n();
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [seedSuccess, setSeedSuccess] = useState<string | null>(null);
    const [seedError, setSeedError] = useState<string | null>(null);
    const [seeding, setSeeding] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [hideMobileFields, setHideMobileFields] = useState(['Tax ID / EIN', 'Company ID']);
    const [spellCheckerEnabled, setSpellCheckerEnabled] = useState(true);
    const [roomChangeRestriction, setRoomChangeRestriction] = useState(true);
    const [webhookUrl, setWebhookUrl] = useState('https://example.com/webhooks/elen1');
    const apiKeyPreview = 'elen_live_••••••••••••_1042';

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!loading) {
            setErrors({});
        }
    }, [loading]);

    function validateProfile(profile: SettingsState['profile']) {
        const next: Record<string, string> = {};
        if (!profile.tenantName.trim()) next.tenantName = t('Tenant / display name is required');
        if (!profile.legalName.trim()) next.legalName = t('Legal name is required');
        if (!profile.phone.trim()) next.phone = t('Primary phone is required');
        if (!profile.email.trim()) next.email = t('General email is required');
        else if (!emailPattern.test(profile.email)) next.email = t('Enter a valid email address');
        if (!profile.addressLine1.trim()) next.addressLine1 = t('Address line 1 is required');
        if (!profile.city.trim()) next.city = t('City is required');
        if (!profile.state.trim()) next.state = t('State / Province is required');
        if (!profile.postalCode.trim()) next.postalCode = t('Postal code is required');
        if (!profile.country.trim()) next.country = t('Country is required');
        if (!profile.timeZone.trim()) next.timeZone = t('Time zone is required');
        if (!profile.defaultCurrency.trim()) next.defaultCurrency = t('Currency is required');
        if (!profile.defaultLanguage) next.defaultLanguage = t('Default language is required');
        if (profile.defaultTaxRate.trim()) {
            const parsedDefaultTaxRate = Number(profile.defaultTaxRate);
            if (!Number.isFinite(parsedDefaultTaxRate) || parsedDefaultTaxRate < 0) {
                next.defaultTaxRate = t('Default invoice tax must be a valid non-negative number.');
            }
        }
        if (profile.domainLanguageDefaults.trim()) {
            try {
                JSON.parse(profile.domainLanguageDefaults);
            } catch {
                next.domainLanguageDefaults = t('Domain language defaults must be valid JSON.');
            }
        }
        if (profile.website && !urlPattern.test(profile.website)) next.website = t('Website must include http(s)://');
        if (profile.generalSettings.ownershipTransferTargetEmail && !emailPattern.test(profile.generalSettings.ownershipTransferTargetEmail)) {
            next.ownershipTransferTargetEmail = t('Enter a valid email address');
        }
        return next;
    }

    async function handleSave() {
        setSuccess(null);
        const validationErrors = validateProfile(settings.profile);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length) return;

        setSaving(true);
        try {
            await save(settings);
            setSuccess(t('Saved successfully'));
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    }

    async function handleSeedDemoData() {
        const confirmed = window.confirm(
            t('Reset and seed demo data for this organization? This clears existing tenant data (except users and organization) and recreates demo records.'),
        );
        if (!confirmed) return;

        setSeedSuccess(null);
        setSeedError(null);
        setSeeding(true);
        try {
            const result = await fetchJson<{
                status: 'ok';
                summary: {
                    cleared: true;
                    createdProviders: number;
                    createdPatients: number;
                    createdProviderProfiles: number;
                    createdWalkInPatients: number;
                    createdUrgentCareVisits: number;
                    createdAppointmentTypes: number;
                    createdFutureAppointments: number;
                    createdPastAppointments: number;
                    createdLabReports: number;
                    createdCatalogServices: number;
                    createdCatalogProducts: number;
                    createdInvoices: number;
                    createdPrescriptions: number;
                    createdTreatmentPlans: number;
                    createdDependentLinks: number;
                    createdPortalThreads: number;
                    createdPortalMessages: number;
                    createdInternalMessageThreads: number;
                    createdInternalMessages: number;
                    createdHealthSignalDefinitions: number;
                    createdHealthSignalPoints: number;
                    createdHealthAlerts: number;
                    createdHealthCases: number;
                    createdHealthContacts: number;
                    createdHealthClusters: number;
                    createdCapacitySnapshots: number;
                    createdHealthInterventions: number;
                    createdHealthReports: number;
                    totals: {
                        providers: number;
                        patients: number;
                        appointments: number;
                        appointmentTypes: number;
                        invoices: number;
                        prescriptions: number;
                        urgentCareVisits: number;
                        labReports: number;
                        catalogServices: number;
                        catalogProducts: number;
                        treatmentPlans: number;
                        portalThreads: number;
                        internalMessageThreads: number;
                        internalMessages: number;
                        healthSignalDefinitions: number;
                        healthSignalPoints: number;
                        healthAlerts: number;
                        healthCases: number;
                        healthContacts: number;
                        healthClusters: number;
                        capacitySnapshots: number;
                        healthInterventions: number;
                        healthReports: number;
                    };
                };
            }>('/settings/seed-demo', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirmReset: true }),
            });

            setSeedSuccess(
                t('Seed complete: {providers} providers, {patients} patients, {appointments} appointments, and {invoices} invoices.')
                    .replace('{providers}', String(result.summary.totals.providers))
                    .replace('{patients}', String(result.summary.totals.patients))
                    .replace('{appointments}', String(result.summary.totals.appointments))
                    .replace('{invoices}', String(result.summary.totals.invoices)),
            );
            setTimeout(() => setSeedSuccess(null), 8000);
        } catch (err: any) {
            setSeedError(err?.message || t('Failed to seed demo data.'));
        } finally {
            setSeeding(false);
        }
    }

    function updateField(updater: (prev: SettingsState['profile']) => SettingsState['profile'], touched?: keyof SettingsState['profile']) {
        setSettings((prev) => ({ ...prev, profile: updater(prev.profile) }));
        if (touched) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[touched];
                return next;
            });
        }
    }

    function updateSocialMediaAccount(key: string, value: string) {
        updateField((prev) => ({
            ...prev,
            socialMediaAccounts: {
                ...prev.socialMediaAccounts,
                [key]: value,
            },
        }));
    }

    function addLetterTemplate(type: 'header' | 'footer') {
        const key = type === 'header' ? 'letterHeaderTemplates' : 'letterFooterTemplates';
        updateField((prev) => ({
            ...prev,
            [key]: [
                ...prev[key],
                {
                    id: `${type}-${Date.now()}`,
                    name: type === 'header' ? t('New header') : t('New footer'),
                    content: type === 'header' ? '{{companyLogo}}\n{{companyName}}' : 'Confidential clinical communication',
                    isDefault: prev[key].length === 0,
                },
            ],
        }));
    }

    function updateLetterTemplate(type: 'header' | 'footer', templateId: string, patch: Partial<{ name: string; content: string; isDefault: boolean }>) {
        const key = type === 'header' ? 'letterHeaderTemplates' : 'letterFooterTemplates';
        updateField((prev) => ({
            ...prev,
            [key]: prev[key].map((template) => {
                if (template.id !== templateId) {
                    return patch.isDefault ? { ...template, isDefault: false } : template;
                }
                return { ...template, ...patch };
            }),
        }));
    }

    function removeLetterTemplate(type: 'header' | 'footer', templateId: string) {
        const key = type === 'header' ? 'letterHeaderTemplates' : 'letterFooterTemplates';
        updateField((prev) => ({
            ...prev,
            [key]: prev[key].filter((template) => template.id !== templateId),
        }));
    }

    function updateAccountSecurity(patch: Partial<SettingsState['profile']['accountSecurity']>) {
        updateField((prev) => ({
            ...prev,
            accountSecurity: {
                ...prev.accountSecurity,
                ...patch,
            },
        }));
    }

    function updateGeneralSettings(patch: Partial<SettingsState['profile']['generalSettings']>) {
        updateField((prev) => ({
            ...prev,
            generalSettings: {
                ...prev.generalSettings,
                ...patch,
            },
        }));
    }

    function updateDataSettings(patch: Partial<SettingsState['profile']['dataSettings']>) {
        updateField((prev) => ({
            ...prev,
            dataSettings: {
                ...prev.dataSettings,
                ...patch,
            },
        }));
    }

    function makeDataId(prefix: string) {
        return `${prefix}-${Date.now()}`;
    }

    function addSearchRule() {
        const recordType = window.prompt(t('Record type'), 'Products');
        if (!recordType) return;
        const fields = window.prompt(t('Visible fields, comma separated'), 'Name, Category, Status') || '';
        updateDataSettings({
            searchResultCustomization: [
                ...settings.profile.dataSettings.searchResultCustomization,
                { id: makeDataId('search'), recordType: recordType.trim(), displayName: recordType.trim(), fields: fields.split(',').map((item) => item.trim()).filter(Boolean), enabled: true },
            ],
        });
    }

    function updateSearchRule(id: string, patch: Partial<SearchResultRule>) {
        updateDataSettings({
            searchResultCustomization: settings.profile.dataSettings.searchResultCustomization.map((rule) => rule.id === id ? { ...rule, ...patch } : rule),
        });
    }

    function deleteSearchRule(id: string) {
        updateDataSettings({
            searchResultCustomization: settings.profile.dataSettings.searchResultCustomization.filter((rule) => rule.id !== id),
        });
    }

    function addCustomField() {
        const name = window.prompt(t('Custom field name'), 'Membership number');
        if (!name) return;
        const entity = window.prompt(t('Applies to'), 'Client') || 'Client';
        updateDataSettings({
            customFields: [
                ...settings.profile.dataSettings.customFields,
                { id: makeDataId('custom'), name: name.trim(), entity: entity.trim(), type: 'text', required: false },
            ],
        });
    }

    function updateCustomField(id: string, patch: Partial<CustomDataField>) {
        updateDataSettings({
            customFields: settings.profile.dataSettings.customFields.map((field) => field.id === id ? { ...field, ...patch } : field),
        });
    }

    function deleteCustomField(id: string) {
        updateDataSettings({
            customFields: settings.profile.dataSettings.customFields.filter((field) => field.id !== id),
        });
    }

    function addLabel() {
        const name = window.prompt(t('Label name'), 'Follow up');
        if (!name) return;
        updateDataSettings({
            labels: [
                ...settings.profile.dataSettings.labels,
                { id: makeDataId('label'), name: name.trim(), color: '#475569', appliesTo: 'Clients' },
            ],
        });
    }

    function updateLabel(id: string, patch: Partial<DataLabel>) {
        updateDataSettings({
            labels: settings.profile.dataSettings.labels.map((label) => label.id === id ? { ...label, ...patch } : label),
        });
    }

    function deleteLabel(id: string) {
        updateDataSettings({
            labels: settings.profile.dataSettings.labels.filter((label) => label.id !== id),
        });
    }

    function addImportantField() {
        const name = window.prompt(t('Important field name'), 'Medication alerts');
        if (!name) return;
        updateDataSettings({
            importantFields: [
                ...settings.profile.dataSettings.importantFields,
                { id: makeDataId('important'), name: name.trim(), entity: 'Client', reason: t('Shown in high-priority clinical and admin contexts.') },
            ],
        });
    }

    function updateImportantField(id: string, patch: Partial<ImportantDataField>) {
        updateDataSettings({
            importantFields: settings.profile.dataSettings.importantFields.map((field) => field.id === id ? { ...field, ...patch } : field),
        });
    }

    function deleteImportantField(id: string) {
        updateDataSettings({
            importantFields: settings.profile.dataSettings.importantFields.filter((field) => field.id !== id),
        });
    }

    function startOwnershipTransfer() {
        const targetEmail = settings.profile.generalSettings.ownershipTransferTargetEmail.trim();
        if (!emailPattern.test(targetEmail)) {
            setErrors((prev) => ({ ...prev, ownershipTransferTargetEmail: t('Enter a valid email address') }));
            return;
        }
        updateGeneralSettings({
            accountOwnerEmail: settings.profile.generalSettings.accountOwnerEmail || settings.profile.email,
            ownershipTransferTargetEmail: targetEmail,
            ownershipTransferStatus: 'PENDING',
        });
        setSuccess(t('Ownership transfer prepared. Save changes to persist it.'));
    }

    function cancelOwnershipTransfer() {
        updateGeneralSettings({
            ownershipTransferTargetEmail: '',
            ownershipTransferStatus: 'CANCELLED',
        });
    }

    function updateSubscriptionCard() {
        const nextCard = window.prompt(t('Enter the new billing card label'), settings.profile.generalSettings.subscriptionCardLabel);
        if (!nextCard) return;
        updateGeneralSettings({ subscriptionCardLabel: nextCard.trim() });
    }

    function downloadSubscriptionInvoice(invoice: SubscriptionInvoice) {
        const payload = {
            invoiceId: invoice.id,
            period: invoice.period,
            amount: invoice.amount,
            currency: invoice.currency,
            status: invoice.status,
            issuedAt: invoice.issuedAt,
            organization: settings.profile.tenantName || settings.profile.legalName,
            generatedAt: new Date().toISOString(),
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoice.id}.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    function formatInvoiceAmount(invoice: SubscriptionInvoice) {
        return formatMoney(invoice.amount, { currency: invoice.currency || undefined });
    }

    async function uploadOrganizationLogo(file?: File) {
        if (!file) return;
        setLogoUploadError(null);

        const allowedTypes = ['image/png', 'image/jpeg'];
        if (!allowedTypes.includes(file.type)) {
            setLogoUploadError(t('Logo must be a PNG or JPEG image.'));
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setLogoUploadError(t('Logo file must be 5MB or smaller.'));
            return;
        }

        setUploadingLogo(true);
        try {
            const presign = await fetchJson<{ key: string; uploadUrl: string; url: string }>('/storage/presign', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fileName: file.name,
                    contentType: file.type,
                    kind: 'organization-logo',
                }),
            });

            const uploadResponse = await fetch(presign.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });
            if (!uploadResponse.ok) {
                throw new Error(`Upload failed (${uploadResponse.status})`);
            }

            updateField((prev) => ({ ...prev, logoUrl: presign.url }));
        } catch (err: any) {
            setLogoUploadError(err?.message || t('Logo upload failed.'));
        } finally {
            setUploadingLogo(false);
        }
    }

    if (loading) return <div className="p-6">{t('common.loading', 'Loading...')}</div>;
    if (error) return <div className="p-6 text-rose-600">{t('Error')}: {error}</div>;

    const p = settings.profile;

    return (
        <div className="space-y-6 pb-10">
            {/* Header */}
            <div className="flex items-center justify-between sticky top-0 bg-slate-50/80 backdrop-blur-sm py-4 z-10 border-b border-slate-200/50 -mx-6 px-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                        <ArrowLeft size={20} className="text-slate-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-ink">{t('Business Details')}</h1>
                        <p className="text-slate-500 text-sm">{t('Manage core business information, branding, templates, and account details.')}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {success && <span className="text-emerald-600 text-sm font-medium animate-in fade-in slide-in-from-right-4">{success}</span>}
                    <button
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleSeedDemoData}
                        disabled={seeding}
                    >
                        {seeding ? <Loader2 size={18} className="animate-spin" /> : <Database size={18} />}
                        {seeding ? t('Seeding...') : t('Seed Demo Data')}
                    </button>
                    <button
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ink text-white shadow-lg shadow-ink/20 hover:bg-ink/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        <Save size={18} /> {saving ? t('Saving...') : t('Save Changes')}
                    </button>
                </div>
            </div>

            {(seedSuccess || seedError) && (
                <div className={`card p-4 text-sm ${seedError ? 'border border-rose-200 text-rose-700 bg-rose-50' : 'border border-emerald-200 text-emerald-700 bg-emerald-50'}`}>
                    {seedError || seedSuccess}
                </div>
            )}

            <div className="card p-6 space-y-8 max-w-6xl">
                {/* Identity Section */}
                <div className="space-y-4">
                    <SectionHeader icon={Building} title={t('Business Profile')} description={t('Core business information and account details used across Elen1.')} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label={t('Tenant Name (Display Name)')} required error={errors.tenantName} value={p.tenantName} onChange={(v) => updateField((prev) => ({ ...prev, tenantName: v }), 'tenantName')} placeholder={t('e.g. NeoHealth Group')} />
                        <Input label={t('Legal Name')} required error={errors.legalName} value={p.legalName} onChange={(v) => updateField((prev) => ({ ...prev, legalName: v }), 'legalName')} placeholder={t('e.g. NeoHealth Services, Inc.')} />
                        <Input label={t('Slogan / Tagline')} value={p.slogan} onChange={(v) => updateField((prev) => ({ ...prev, slogan: v }))} placeholder={t('e.g. Care for the Future')} />
                        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <p className="font-medium text-slate-900">{t('Company ID')}</p>
                                    <p className="font-mono text-xs text-slate-500">{p.companyId || t('Unavailable')}</p>
                                </div>
                                <button className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100" type="button" onClick={() => navigator.clipboard?.writeText(p.companyId)}>
                                    <Copy size={14} /> {t('Copy')}
                                </button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Input label={t('Logo URL')} value={p.logoUrl} onChange={(v) => updateField((prev) => ({ ...prev, logoUrl: v }))} placeholder="https://..." />
                            <div className="flex items-center gap-2">
                                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm cursor-pointer hover:bg-slate-50">
                                    {uploadingLogo ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                                    {uploadingLogo ? t('Uploading...') : t('Upload Logo')}
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/png,image/jpeg"
                                        onChange={(event) => {
                                            const selected = event.target.files?.[0];
                                            event.currentTarget.value = '';
                                            uploadOrganizationLogo(selected);
                                        }}
                                    />
                                </label>
                                {p.logoUrl && (
                                    <button
                                        type="button"
                                        className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
                                        onClick={() => updateField((prev) => ({ ...prev, logoUrl: '' }))}
                                    >
                                        {t('Remove')}
                                    </button>
                                )}
                            </div>
                            {logoUploadError && <p className="text-xs text-rose-600">{logoUploadError}</p>}
                            {p.logoUrl && (
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                                    <img src={p.logoUrl} alt={t('Organization logo')} className="h-16 w-auto object-contain" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="border-t border-slate-100 pt-4 space-y-4">
                    <SectionHeader icon={Globe} title={t('Contact & Regional')} description={t('Address and localization.')} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label={t('Official Website')} value={p.website} error={errors.website} onChange={(v) => updateField((prev) => ({ ...prev, website: v }), 'website')} placeholder="https://neohealth.com" />
                        <Input label={t('Primary Phone')} required value={p.phone} error={errors.phone} onChange={(v) => updateField((prev) => ({ ...prev, phone: v }), 'phone')} type="tel" />
                        <Input label={t('General Email')} required value={p.email} error={errors.email} onChange={(v) => updateField((prev) => ({ ...prev, email: v }), 'email')} type="email" />
                        <Input label={t('Time Zone')} required value={p.timeZone} error={errors.timeZone} onChange={(v) => updateField((prev) => ({ ...prev, timeZone: v }), 'timeZone')} placeholder="UTC" />
                        <Input label={t('Currency')} required value={p.defaultCurrency} error={errors.defaultCurrency} onChange={(v) => updateField((prev) => ({ ...prev, defaultCurrency: v }), 'defaultCurrency')} />
                        <div className="md:col-span-2">
                            <AddressFields
                                value={{
                                    line1: p.addressLine1,
                                    line2: p.addressLine2,
                                    city: p.city,
                                    state: p.state,
                                    postalCode: p.postalCode,
                                    country: p.country,
                                    formattedAddress: p.formattedAddress,
                                    googlePlaceId: p.googlePlaceId,
                                    latitude: p.latitude ? Number(p.latitude) : null,
                                    longitude: p.longitude ? Number(p.longitude) : null,
                                    source: p.source as 'MANUAL' | 'GOOGLE' | 'IMPORTED',
                                }}
                                onChange={(next) => {
                                    updateField((prev) => ({
                                        ...prev,
                                        addressLine1: next.line1,
                                        addressLine2: next.line2,
                                        city: next.city,
                                        state: next.state,
                                        postalCode: next.postalCode,
                                        country: next.country,
                                        formattedAddress: next.formattedAddress ?? '',
                                        googlePlaceId: next.googlePlaceId ?? '',
                                        latitude: next.latitude != null ? String(next.latitude) : '',
                                        longitude: next.longitude != null ? String(next.longitude) : '',
                                        source: (next.source ?? 'MANUAL') as 'MANUAL' | 'GOOGLE' | 'IMPORTED',
                                    }));
                                    setErrors((prevErrors) => {
                                        const nextErrors = { ...prevErrors };
                                        delete nextErrors.addressLine1;
                                        delete nextErrors.addressLine2;
                                        delete nextErrors.city;
                                        delete nextErrors.state;
                                        delete nextErrors.postalCode;
                                        delete nextErrors.country;
                                        return nextErrors;
                                    });
                                }}
                                errors={{
                                    line1: errors.addressLine1,
                                    city: errors.city,
                                    state: errors.state,
                                    postalCode: errors.postalCode,
                                    country: errors.country,
                                }}
                                labels={{
                                    line1: t('Address Line 1'),
                                    line2: t('Address Line 2'),
                                    city: t('City'),
                                    state: t('State / Province'),
                                    postalCode: t('Postal Code'),
                                    country: t('Country'),
                                }}
                            />
                        </div>
                        <label className="text-sm space-y-1 block">
                            <span className="text-slate-500 font-medium flex items-center gap-1">
                                {t('profile.orgDefaultLanguage', 'Organization Default Language')} <span className="text-rose-600">*</span>
                            </span>
                            <select
                                className={`w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-ink/10 focus:border-ink transition-all outline-none ${errors.defaultLanguage ? 'border-rose-300' : 'border-slate-200'}`}
                                value={p.defaultLanguage}
                                onChange={(e) => updateField((prev) => ({ ...prev, defaultLanguage: e.target.value as AppLanguage }), 'defaultLanguage')}
                            >
                                {supportedLanguages.map((language) => (
                                    <option key={language} value={language}>
                                        {languageLabels[language]}
                                    </option>
                                ))}
                            </select>
                            {errors.defaultLanguage && <p className="text-xs text-rose-600">{errors.defaultLanguage}</p>}
                        </label>
                        <Input
                            label={t('profile.enabledLanguages', 'Enabled Languages')}
                            value={p.enabledLanguages}
                            onChange={(v) => updateField((prev) => ({ ...prev, enabledLanguages: v }))}
                            placeholder="en, es"
                        />
                        <label className="text-sm space-y-1 block md:col-span-2">
                            <span className="text-slate-500 font-medium">{t('profile.domainLanguageDefaults', 'Domain Language Defaults')}</span>
                            <textarea
                                className={`w-full border rounded-lg px-3 py-2 bg-white h-24 text-sm resize-none ${errors.domainLanguageDefaults ? 'border-rose-300' : 'border-slate-200'}`}
                                value={p.domainLanguageDefaults}
                                onChange={(e) => updateField((prev) => ({ ...prev, domainLanguageDefaults: e.target.value }), 'domainLanguageDefaults')}
                                placeholder='{\n  "app.elen1.com":"en",\n  "mi.elen1.com":"es"\n}'
                            />
                            {errors.domainLanguageDefaults && <p className="text-xs text-rose-600">{errors.domainLanguageDefaults}</p>}
                        </label>
                    </div>
                </div>

                <div className="border-t border-slate-100 pt-4 space-y-4">
                    <SectionHeader icon={ShieldCheck} title={t('Security, Billing & Admin')} description={t('Operational settings for account security, subscriptions, and practice controls.')} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <SetupCard icon={KeyRound} title={t('Two-factor authentication')} description={t('Manage primary 2FA, recovery, and account security readiness.')}>
                            <ToggleRow label={t('Enable 2FA for safer account access')} checked={p.accountSecurity.twoFactorEnabled} onChange={(twoFactorEnabled) => updateAccountSecurity({ twoFactorEnabled })} />
                            <ToggleRow label={t('Require 2FA for admins')} checked={p.accountSecurity.enforceForAdmins} onChange={(enforceForAdmins) => updateAccountSecurity({ enforceForAdmins })} />
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{t('Primary 2FA method')}</span>
                                <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={p.accountSecurity.primaryTwoFactorMethod} onChange={(event) => updateAccountSecurity({ primaryTwoFactorMethod: event.target.value as 'email' | 'authenticator' })}>
                                    <option value="email">{t('Email')}</option>
                                    <option value="authenticator">{t('Authenticator app')}</option>
                                </select>
                            </label>
                            <Input label={t('Primary 2FA Email')} value={p.accountSecurity.primaryTwoFactorEmail} onChange={(primaryTwoFactorEmail) => updateAccountSecurity({ primaryTwoFactorEmail })} placeholder={p.email || 'security@example.com'} />
                            <Input label={t('2FA Lockout Recovery Email')} value={p.accountSecurity.lockoutRecoveryEmail} onChange={(lockoutRecoveryEmail) => updateAccountSecurity({ lockoutRecoveryEmail })} placeholder={p.email || 'owner@example.com'} />
                            <div className="grid grid-cols-2 gap-3">
                                <Input label={t('Lockout threshold')} type="number" value={String(p.accountSecurity.lockoutThreshold)} onChange={(value) => updateAccountSecurity({ lockoutThreshold: Number(value) || 5 })} />
                                <Input label={t('Passcode expiry minutes')} type="number" value={String(p.accountSecurity.passcodeExpiryMinutes)} onChange={(value) => updateAccountSecurity({ passcodeExpiryMinutes: Number(value) || 10 })} />
                            </div>
                            <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-800">
                                {t('2FA Login Flow')}: {t('After password verification, protected users receive a six-digit authentication code before a session is issued.')}
                            </div>
                            <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-sm text-amber-800">
                                {t('2FA Passcode Troubleshooting')}: {t('Check spam folders, confirm the primary 2FA email, then request a new code before repeated retries trigger lockout.')}
                            </div>
                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">
                                <p className="font-semibold text-slate-900">{t('Data Protection Guidance')}</p>
                                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                                    {p.accountSecurity.dataProtectionGuidance.map((item) => <li key={item}>{t(item)}</li>)}
                                </ul>
                            </div>
                        </SetupCard>

                        <SetupCard icon={Image} title={t('Media storage')} description={t('Control HQ photos, social profile links, and brand assets.')}>
                            <ToggleRow
                                label={t('Enable high-quality photo storage')}
                                checked={p.hqPhotoStorage.enabled}
                                onChange={(enabled) => updateField((prev) => ({ ...prev, hqPhotoStorage: { ...prev.hqPhotoStorage, enabled } }))}
                            />
                            <Input
                                label={t('HQ photo storage folder')}
                                value={p.hqPhotoStorage.folder}
                                onChange={(folder) => updateField((prev) => ({ ...prev, hqPhotoStorage: { ...prev.hqPhotoStorage, folder } }))}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <label className="text-sm space-y-1 block">
                                    <span className="text-slate-500 font-medium">{t('Photo quality')}</span>
                                    <select
                                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                                        value={p.hqPhotoStorage.quality}
                                        onChange={(event) => updateField((prev) => ({ ...prev, hqPhotoStorage: { ...prev.hqPhotoStorage, quality: event.target.value as 'original' | 'optimized' } }))}
                                    >
                                        <option value="original">{t('Original quality')}</option>
                                        <option value="optimized">{t('Optimized')}</option>
                                    </select>
                                </label>
                                <Input
                                    label={t('Retention days')}
                                    type="number"
                                    value={String(p.hqPhotoStorage.retentionDays)}
                                    onChange={(value) => updateField((prev) => ({ ...prev, hqPhotoStorage: { ...prev.hqPhotoStorage, retentionDays: Number(value) || 0 } }))}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {[
                                    ['instagram', 'Instagram'],
                                    ['facebook', 'Facebook'],
                                    ['linkedIn', 'LinkedIn'],
                                    ['x', 'X / Twitter'],
                                    ['tiktok', 'TikTok'],
                                    ['youtube', 'YouTube'],
                                ].map(([key, label]) => (
                                    <Input
                                        key={key}
                                        label={t(label)}
                                        value={p.socialMediaAccounts[key] || ''}
                                        onChange={(value) => updateSocialMediaAccount(key, value)}
                                        placeholder={key === 'instagram' ? '@elenclinic' : 'https://'}
                                    />
                                ))}
                            </div>
                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
                                {t('Logo upload is available above; social links and HQ photo storage settings are saved with the business profile.')}
                            </div>
                        </SetupCard>

                        <SetupCard icon={FileText} title={t('Letters & documents')} description={t('Set letter headers, footers, and document identity details.')}>
                            <TemplateEditor
                                title={t('Letter Header Templates')}
                                templates={p.letterHeaderTemplates}
                                onAdd={() => addLetterTemplate('header')}
                                onUpdate={(id, patch) => updateLetterTemplate('header', id, patch)}
                                onRemove={(id) => removeLetterTemplate('header', id)}
                            />
                            <TemplateEditor
                                title={t('Letter Footer Templates')}
                                templates={p.letterFooterTemplates}
                                onAdd={() => addLetterTemplate('footer')}
                                onUpdate={(id, patch) => updateLetterTemplate('footer', id, patch)}
                                onRemove={(id) => removeLetterTemplate('footer', id)}
                            />
                            <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                                <span>{t('Company ID Lookup')}: <strong className="font-mono">{p.companyId || t('Unavailable')}</strong></span>
                                <button className="inline-flex items-center gap-1 text-slate-600 hover:text-ink" type="button" onClick={() => navigator.clipboard?.writeText(p.companyId)}>
                                    <Copy size={14} /> {t('Copy')}
                                </button>
                            </div>
                        </SetupCard>

                        <SetupCard icon={UserRoundCog} title={t('Ownership & access')} description={t('Prepare ownership transfer and advanced account administration.')}>
                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Current owner')}</p>
                                <p className="mt-1 font-medium text-slate-900">{p.generalSettings.accountOwnerEmail || p.email || t('Not set')}</p>
                            </div>
                            <Input
                                label={t('New account owner email')}
                                error={errors.ownershipTransferTargetEmail}
                                value={p.generalSettings.ownershipTransferTargetEmail}
                                onChange={(ownershipTransferTargetEmail) => {
                                    updateGeneralSettings({ ownershipTransferTargetEmail });
                                    setErrors((prev) => {
                                        const next = { ...prev };
                                        delete next.ownershipTransferTargetEmail;
                                        return next;
                                    });
                                }}
                                placeholder="owner@example.com"
                            />
                            <div className="flex flex-wrap gap-2">
                                <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" onClick={startOwnershipTransfer}>
                                    {t('Start ownership transfer')}
                                </button>
                                {p.generalSettings.ownershipTransferStatus === 'PENDING' && (
                                    <button type="button" className="rounded-lg border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50" onClick={cancelOwnershipTransfer}>
                                        {t('Cancel transfer')}
                                    </button>
                                )}
                            </div>
                            <div className="rounded-lg border border-slate-100 bg-white p-3 text-sm text-slate-700">
                                {t('Transfer status')}: <strong>{t(p.generalSettings.ownershipTransferStatus)}</strong>
                            </div>
                            <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-sm text-amber-800">
                                {t('Transfers require owner confirmation and admin audit logging.')}
                            </div>
                        </SetupCard>

                        <SetupCard icon={CreditCard} title={t('Billing & subscription')} description={t('Enable insurance billing and manage plan, invoice, and card settings.')}>
                            <ToggleRow label={t('Enable insurance billing')} checked={p.generalSettings.insuranceBillingEnabled} onChange={(insuranceBillingEnabled) => updateGeneralSettings({ insuranceBillingEnabled })} />
                            <ToggleRow label={t('Marketing Plus')} checked={p.generalSettings.marketingPlusEnabled} onChange={(marketingPlusEnabled) => updateGeneralSettings({ marketingPlusEnabled })} />
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{t('Subscription Upgrade')}</span>
                                <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={p.generalSettings.subscriptionPlan} onChange={(event) => updateGeneralSettings({ subscriptionPlan: event.target.value as SettingsState['profile']['generalSettings']['subscriptionPlan'] })}>
                                    <option value="simple">{t('Simple EMR')}</option>
                                    <option value="growth">{t('Growth')}</option>
                                    <option value="enterprise">{t('Enterprise')}</option>
                                </select>
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">{t('Plan')}: <strong>{t(p.generalSettings.subscriptionPlan)}</strong></div>
                                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">{t('Next invoice')}: <strong>{t('Monthly')}</strong></div>
                                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">{t('Card')}: <strong>{p.generalSettings.subscriptionCardLabel || t('Not set')}</strong></div>
                            </div>
                            <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" onClick={updateSubscriptionCard}>
                                {t('Subscription Card Update')}
                            </button>
                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                                <p className="text-sm font-semibold text-slate-900">{t('Subscription Invoice Download')}</p>
                                <div className="mt-2 space-y-2">
                                    {p.generalSettings.subscriptionInvoices.map((invoice) => (
                                        <div key={invoice.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm">
                                            <span>{invoice.period} · {formatInvoiceAmount(invoice)} · {t(invoice.status)}</span>
                                            <button type="button" className="rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={() => downloadSubscriptionInvoice(invoice)}>
                                                {t('Download')}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </SetupCard>

                        <SetupCard icon={Languages} title={t('Language management')} description={t('Control system languages and defaults for domains.')}>
                            <label className="text-sm space-y-1 block">
                                <span className="text-slate-500 font-medium">{t('System Language')}</span>
                                <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={p.defaultLanguage} onChange={(event) => updateField((prev) => ({ ...prev, defaultLanguage: event.target.value as AppLanguage }), 'defaultLanguage')}>
                                    {supportedLanguages.map((language) => (
                                        <option key={language} value={language}>{languageLabels[language]}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {supportedLanguages.map((language) => (
                                    <span key={language} className={`rounded-full border px-3 py-1 text-xs font-medium ${p.enabledLanguages.includes(language) ? 'border-ink bg-ink text-white' : 'border-slate-200 bg-white text-slate-600'}`}>
                                        {languageLabels[language]}
                                    </span>
                                ))}
                            </div>
                            <p className="text-xs text-slate-500">{t('Use Enabled Languages and Domain Language Defaults above for persistence.')}</p>
                        </SetupCard>

                        <SetupCard icon={ListChecks} title={t('Dashboard Data')} description={t('View and understand dashboard metrics used across the account.')}>
                            <textarea
                                className="min-h-[100px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                                value={p.generalSettings.dashboardDataNotes}
                                onChange={(event) => updateGeneralSettings({ dashboardDataNotes: event.target.value })}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                {['Active clients', 'Booked appointments', 'Completed forms', 'Revenue', 'Outstanding balances', 'Marketing responses'].map((metric) => (
                                    <div key={metric} className="rounded-lg border border-slate-100 bg-slate-50 p-3">{t(metric)}</div>
                                ))}
                            </div>
                        </SetupCard>

                        <SetupCard icon={Search} title={t('Search customization')} description={t('Choose which record types appear in global search results.')}>
                            <div className="space-y-2">
                                {p.dataSettings.searchResultCustomization.map((rule) => (
                                    <div key={rule.id} className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 md:grid-cols-[1fr_1.4fr_auto]">
                                        <Input label={t('Record type')} value={rule.recordType} onChange={(recordType) => updateSearchRule(rule.id, { recordType, displayName: recordType })} />
                                        <Input label={t('Visible fields')} value={rule.fields.join(', ')} onChange={(value) => updateSearchRule(rule.id, { fields: value.split(',').map((item) => item.trim()).filter(Boolean) })} />
                                        <div className="flex items-end gap-2">
                                            <label className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700">
                                                <input type="checkbox" checked={rule.enabled} onChange={(event) => updateSearchRule(rule.id, { enabled: event.target.checked })} className="accent-ink" />
                                                {t('Show')}
                                            </label>
                                            <button type="button" className="h-10 rounded-lg border border-rose-200 bg-white px-3 text-xs font-medium text-rose-600 hover:bg-rose-50" onClick={() => deleteSearchRule(rule.id)}>
                                                {t('Delete')}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" onClick={addSearchRule}>
                                {t('Add search result type')}
                            </button>
                        </SetupCard>

                        <SetupCard icon={Tags} title={t('Custom labels & fields')} description={t('Manage global labels and custom field placeholders.')}>
                            <div className="space-y-3">
                                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <p className="text-sm font-semibold text-slate-900">{t('Custom Fields')}</p>
                                        <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={addCustomField}>{t('Create')}</button>
                                    </div>
                                    <div className="space-y-2">
                                        {p.dataSettings.customFields.map((field) => (
                                            <div key={field.id} className="grid gap-2 rounded-lg border border-slate-100 bg-white p-2 md:grid-cols-[1fr_0.8fr_0.8fr_auto]">
                                                <Input label={t('Name')} value={field.name} onChange={(name) => updateCustomField(field.id, { name })} />
                                                <Input label={t('Entity')} value={field.entity} onChange={(entity) => updateCustomField(field.id, { entity })} />
                                                <label className="text-sm space-y-1 block">
                                                    <span className="text-slate-500 font-medium">{t('Type')}</span>
                                                    <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={field.type} onChange={(event) => updateCustomField(field.id, { type: event.target.value as CustomDataField['type'] })}>
                                                        <option value="text">{t('Text')}</option>
                                                        <option value="number">{t('Number')}</option>
                                                        <option value="date">{t('Date')}</option>
                                                        <option value="select">{t('Select')}</option>
                                                        <option value="checkbox">{t('Checkbox')}</option>
                                                    </select>
                                                </label>
                                                <div className="flex items-end gap-2">
                                                    <label className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700">
                                                        <input type="checkbox" checked={field.required} onChange={(event) => updateCustomField(field.id, { required: event.target.checked })} className="accent-ink" />
                                                        {t('Required')}
                                                    </label>
                                                    <button type="button" className="h-10 rounded-lg border border-rose-200 bg-white px-3 text-xs font-medium text-rose-600 hover:bg-rose-50" onClick={() => deleteCustomField(field.id)}>
                                                        {t('Delete')}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <p className="text-sm font-semibold text-slate-900">{t('Labels')}</p>
                                        <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={addLabel}>{t('Create')}</button>
                                    </div>
                                    <div className="space-y-2">
                                        {p.dataSettings.labels.map((label) => (
                                            <div key={label.id} className="grid gap-2 rounded-lg border border-slate-100 bg-white p-2 md:grid-cols-[1fr_0.8fr_auto_auto]">
                                                <Input label={t('Name')} value={label.name} onChange={(name) => updateLabel(label.id, { name })} />
                                                <Input label={t('Applies to')} value={label.appliesTo} onChange={(appliesTo) => updateLabel(label.id, { appliesTo })} />
                                                <label className="text-sm space-y-1 block">
                                                    <span className="text-slate-500 font-medium">{t('Color')}</span>
                                                    <input type="color" className="h-10 w-full rounded-lg border border-slate-200 bg-white p-1" value={label.color} onChange={(event) => updateLabel(label.id, { color: event.target.value })} />
                                                </label>
                                                <div className="flex items-end">
                                                    <button type="button" className="h-10 rounded-lg border border-rose-200 bg-white px-3 text-xs font-medium text-rose-600 hover:bg-rose-50" onClick={() => deleteLabel(label.id)}>
                                                        {t('Delete')}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </SetupCard>

                        <SetupCard icon={ListChecks} title={t('Important Fields')} description={t('Create and manage important data fields shown in high-priority workflows.')}>
                            <div className="space-y-2">
                                {p.dataSettings.importantFields.map((field) => (
                                    <div key={field.id} className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 md:grid-cols-[1fr_0.8fr_1.2fr_auto]">
                                        <Input label={t('Name')} value={field.name} onChange={(name) => updateImportantField(field.id, { name })} />
                                        <Input label={t('Entity')} value={field.entity} onChange={(entity) => updateImportantField(field.id, { entity })} />
                                        <Input label={t('Reason')} value={field.reason} onChange={(reason) => updateImportantField(field.id, { reason })} />
                                        <div className="flex items-end">
                                            <button type="button" className="h-10 rounded-lg border border-rose-200 bg-white px-3 text-xs font-medium text-rose-600 hover:bg-rose-50" onClick={() => deleteImportantField(field.id)}>
                                                {t('Delete')}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" onClick={addImportantField}>
                                {t('Create important field')}
                            </button>
                        </SetupCard>

                        <SetupCard icon={EyeOff} title={t('Mobile hidden fields')} description={t('Choose fields hidden from mobile staff experiences.')}>
                            <div className="flex flex-wrap gap-2">
                                {['Tax ID / EIN', 'Company ID', 'Subscription', 'Insurance member ID', 'Clinical notes'].map((field) => (
                                    <button
                                        key={field}
                                        type="button"
                                        className={`rounded-full border px-3 py-1 text-xs font-medium ${hideMobileFields.includes(field) ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-slate-200 bg-white text-slate-600'}`}
                                        onClick={() => setHideMobileFields((current) => current.includes(field) ? current.filter((value) => value !== field) : [...current, field])}
                                    >
                                        {t(field)}
                                    </button>
                                ))}
                            </div>
                        </SetupCard>

                        <SetupCard icon={KeyRound} title={t('Developer API keys')} description={t('Display, rotate, and audit API credentials used by integrations.')}>
                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm font-mono text-slate-700">{apiKeyPreview}</div>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">{t('Copy key')}</button>
                                <button type="button" className="rounded-lg bg-ink px-3 py-2 text-sm font-semibold text-white">{t('Rotate key')}</button>
                            </div>
                        </SetupCard>

                        <SetupCard icon={Webhook} title={t('Webhooks')} description={t('Configure event delivery for appointments, clients, invoices, payments, leads, and stock changes.')}>
                            <Input label={t('Endpoint URL')} value={webhookUrl} onChange={setWebhookUrl} />
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                {['appointment.created', 'client.updated', 'invoice.paid', 'payment.refunded', 'lead.converted', 'stock.adjusted'].map((event) => (
                                    <label key={event} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                                        <input type="checkbox" defaultChecked className="accent-ink" />
                                        <span>{event}</span>
                                    </label>
                                ))}
                            </div>
                        </SetupCard>

                        <SetupCard icon={ExternalLink} title={t('App marketplace')} description={t('Connect marketplace apps and billing/finance integrations.')}>
                            <div className="grid grid-cols-1 gap-2 text-sm">
                                {['Stripe payments and wallets', 'Healthcode private medical billing', 'Xero accounting sync'].map((integration) => (
                                    <div key={integration} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-3">
                                        <span className="font-medium text-slate-700">{t(integration)}</span>
                                        <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">{t('Configure')}</button>
                                    </div>
                                ))}
                            </div>
                        </SetupCard>

                        <SetupCard icon={ListChecks} title={t('Operational setup objects')} description={t('Maintain the appointment, activity, finance, and insurance lists admins need.')}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                {[
                                    'Activity types',
                                    'Appointment statuses',
                                    'Blockout options',
                                    'Cancellation reasons',
                                    'Salutations',
                                    'Third parties',
                                    'Issuing companies',
                                    'Price lists',
                                    'Insurance companies',
                                ].map((item) => (
                                    <div key={item} className="rounded-lg border border-slate-100 bg-slate-50 p-3">{t(item)}</div>
                                ))}
                            </div>
                        </SetupCard>

                        <SetupCard icon={SpellCheck} title={t('Editor and room policies')} description={t('Control text behavior and appointment room-change restrictions.')}>
                            <ToggleRow label={t('Enable spell checker in notes and forms')} checked={spellCheckerEnabled} onChange={setSpellCheckerEnabled} />
                            <ToggleRow label={t('Restrict room changes when resources conflict')} checked={roomChangeRestriction} onChange={setRoomChangeRestriction} />
                            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-600">
                                {t('Room-change restrictions are documented for appointment moves, online bookings, package bookings, and staff schedule changes.')}
                            </div>
                        </SetupCard>
                    </div>
                </div>

                <div className="border-t border-slate-100 pt-4 space-y-4">
                    <SectionHeader icon={ShieldCheck} title={t('Compliance & Clinical')} description={t('Regulatory and leadership info.')} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label={t('Tax ID / EIN')} value={p.taxId} onChange={(v) => updateField((prev) => ({ ...prev, taxId: v }))} />
                        <Input label={t('Default Invoice Tax Rate (%)')} error={errors.defaultTaxRate} value={p.defaultTaxRate} onChange={(v) => updateField((prev) => ({ ...prev, defaultTaxRate: v }), 'defaultTaxRate')} placeholder={t('e.g. 7.5')} />
                        <Input label={t('NPI / Registration Number')} value={p.registrationNumber} onChange={(v) => updateField((prev) => ({ ...prev, registrationNumber: v }))} />
                        <Input label={t('Accreditation (e.g. JCI, ISO)')} value={p.accreditation} onChange={(v) => updateField((prev) => ({ ...prev, accreditation: v }))} />
                        <Input label={t('Medical Director')} value={p.medicalDirector} onChange={(v) => updateField((prev) => ({ ...prev, medicalDirector: v }))} placeholder={t('Dr. Name')} />
                        <Input label={t('Privacy Officer')} value={p.privacyOfficer} onChange={(v) => updateField((prev) => ({ ...prev, privacyOfficer: v }))} placeholder={t('Officer Name')} />
                    </div>

                    <div className="pt-2">
                        <label className="text-sm space-y-1">
                            <span className="text-slate-500">{t('Specialties')}</span>
                            <textarea
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white h-20 text-sm resize-none"
                                value={p.specialties}
                                onChange={(e) => updateField((prev) => ({ ...prev, specialties: e.target.value }))}
                                placeholder="Cardiology, Pediatrics, etc."
                            />
                        </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SetupCard icon={ShieldCheck} title={t('Compliance support')} description={t('Support pages and readiness checklists for regulated clinics.')}>
                            <div className="grid gap-2 text-sm">
                                {['HIPAA support page', 'CQC support page', 'Controlled-drug audit readiness', 'Monthly compliance review'].map((item) => (
                                    <div key={item} className="rounded-lg border border-slate-100 bg-slate-50 p-3">{t(item)}</div>
                                ))}
                            </div>
                        </SetupCard>
                        <SetupCard icon={ExternalLink} title={t('System help')} description={t('Troubleshooting center, requirements, and live status shortcuts.')}>
                            <div className="grid gap-2 text-sm">
                                {['System requirements', 'Troubleshooting center', 'System status page', 'Browser and device checks'].map((item) => (
                                    <button key={item} type="button" className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-3 text-left text-slate-700 hover:bg-slate-100">
                                        <span>{t(item)}</span>
                                        <ExternalLink size={14} />
                                    </button>
                                ))}
                            </div>
                        </SetupCard>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TemplateEditor({
    title,
    templates,
    onAdd,
    onUpdate,
    onRemove,
}: {
    title: string;
    templates: Array<{ id: string; name: string; content: string; isDefault?: boolean }>;
    onAdd: () => void;
    onUpdate: (id: string, patch: Partial<{ name: string; content: string; isDefault: boolean }>) => void;
    onRemove: (id: string) => void;
}) {
    const { t } = useI18n();
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-800">{title}</p>
                <button type="button" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={onAdd}>
                    {t('Add')}
                </button>
            </div>
            <div className="space-y-2">
                {templates.map((template) => (
                    <div key={template.id} className="space-y-2 rounded-lg border border-slate-100 bg-slate-50 p-3">
                        <div className="grid gap-2 md:grid-cols-[1fr_auto_auto]">
                            <input
                                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                                value={template.name}
                                onChange={(event) => onUpdate(template.id, { name: event.target.value })}
                            />
                            <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700">
                                <input type="checkbox" checked={Boolean(template.isDefault)} onChange={(event) => onUpdate(template.id, { isDefault: event.target.checked })} className="accent-ink" />
                                {t('Default')}
                            </label>
                            <button type="button" className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-medium text-rose-600 disabled:opacity-40" disabled={templates.length <= 1} onClick={() => onRemove(template.id)}>
                                {t('Remove')}
                            </button>
                        </div>
                        <textarea
                            className="h-24 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                            value={template.content}
                            onChange={(event) => onUpdate(template.id, { content: event.target.value })}
                        />
                    </div>
                ))}
                {!templates.length ? <p className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-500">{t('No templates configured.')}</p> : null}
            </div>
        </div>
    );
}

function SectionHeader({ icon: Icon, title, description }: { icon: any, title: string, description: string }) {
    return (
        <div className="flex items-center gap-3 px-1">
            <div className="h-10 w-10 rounded-lg bg-white border border-slate-200 text-ink flex items-center justify-center shadow-sm">
                <Icon size={20} />
            </div>
            <div>
                <h3 className="font-semibold text-ink">{title}</h3>
                <p className="text-xs text-slate-500">{description}</p>
            </div>
        </div>
    );
}

function SetupCard({ icon: Icon, title, description, children }: { icon: any; title: string; description: string; children: ReactNode }) {
    return (
        <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-ink">
                    <Icon size={18} />
                </div>
                <div>
                    <h4 className="font-semibold text-ink">{title}</h4>
                    <p className="text-xs text-slate-500">{description}</p>
                </div>
            </div>
            {children}
        </section>
    );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
    return (
        <label className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
            <span className="font-medium text-slate-700">{label}</span>
            <input type="checkbox" className="accent-ink" checked={checked} onChange={(event) => onChange(event.target.checked)} />
        </label>
    );
}

function Input({
    label,
    value,
    onChange,
    type = 'text',
    placeholder,
    required,
    error,
    autoComplete,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    type?: string;
    placeholder?: string;
    required?: boolean;
    error?: string;
    autoComplete?: string;
}) {
    return (
        <label className="text-sm space-y-1 block">
            <span className="text-slate-500 font-medium flex items-center gap-1">
                {label} {required && <span className="text-rose-600">*</span>}
            </span>
            <input
                className={`w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-ink/10 focus:border-ink transition-all outline-none ${error ? 'border-rose-300' : 'border-slate-200'}`}
                value={value}
                type={type}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete={autoComplete}
            />
            {error && <p className="text-xs text-rose-600">{error}</p>}
        </label>
    );
}
